import { useState, useEffect } from 'react'
import { Plus, Calendar, MapPin, Trash2, Edit2, FileText } from 'lucide-react'
import { adminApi } from '../../lib/api'
import { LocationPicker, type LocationValue } from '../maps/LocationPicker'
import { toNumericCoordinate } from '../../utils/location'
import { SingleFileUploader } from '../files/SingleFileUploader'
import FileMetadataModal, { type FileMetadataFormValues } from '../files/FileMetadataModal'
import Pagination from '../Pagination'
import type { UploadedFile } from '../../types'
import { useToast } from '../ui/ToastProvider'

function normalizeTags(input: any): string[] {
  if (!input) return []
  if (Array.isArray(input)) return input.filter(Boolean).map(String)
  if (typeof input === 'string') {
    try {
      const parsed = JSON.parse(input)
      if (Array.isArray(parsed)) return parsed.filter(Boolean).map(String)
    } catch {
      return input
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean)
    }
  }
  return []
}

function normalizeEventLocation(rawLocation: any): TripEvent['location'] | undefined {
  if (!rawLocation) return undefined
  let location = rawLocation

  if (typeof rawLocation === 'string') {
    try {
      location = JSON.parse(rawLocation)
    } catch {
      return {
        name: rawLocation,
        address: rawLocation,
      }
    }
  }

  if (typeof location !== 'object' || location === null) {
    return undefined
  }

  const latitude = toNumericCoordinate(location.latitude ?? location.lat)
  const longitude = toNumericCoordinate(location.longitude ?? location.lng)

  return {
    id: location.id,
    name: location.name ?? location.address ?? location.title,
    address: location.address ?? location.formatted_address ?? location.name ?? '',
    latitude: typeof latitude === 'number' ? latitude : undefined,
    longitude: typeof longitude === 'number' ? longitude : undefined,
    lat: typeof latitude === 'number' ? latitude : undefined,
    lng: typeof longitude === 'number' ? longitude : undefined,
  }
}

interface TripEvent {
  id: string
  title: string
  description: string
  startDate: string | number  // Can be ISO string or Unix timestamp
  endDate?: string | number   // Can be ISO string or Unix timestamp
  location?: {
    id?: string
    name?: string
    address?: string
    latitude?: number
    longitude?: number
    // Legacy fields
    lat?: number
    lng?: number
  }
  status: 'upcoming' | 'ongoing' | 'completed' | 'cancelled'
  coverImage?: string
  coverImageFileId?: string
  files?: any[]
  notes?: string
  tags: string[]
}

interface EventsTabProps {
  tripId: string
  preloadedData?: any[]
  userId?: string // Waybook user ID to link files to
}

interface EventFormState {
  title: string
  description: string
  startDate: string
  endDate: string
  status: TripEvent['status']
  coverImage: string
  coverImageFileId: string
  tags: string[]
  location: LocationValue
}

export default function EventsTab({ tripId, preloadedData, userId }: EventsTabProps) {
  const { showToast } = useToast()
  const [events, setEvents] = useState<TripEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingEvent, setEditingEvent] = useState<TripEvent | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const itemsPerPage = 10

  const [formData, setFormData] = useState<EventFormState>({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    status: 'upcoming' as TripEvent['status'],
    coverImage: '',
    coverImageFileId: '',
    tags: [],
    location: {
      address: '',
      lat: null as number | null,
      lng: null as number | null,
    },
  })
  const [isEventFileModalOpen, setIsEventFileModalOpen] = useState(false)

  const transformEvent = (event: any): TripEvent => {
    const location = normalizeEventLocation(event.location || event.location_json || event.locationJson)
    const startDate = event.start_date ?? event.startDate
    const endDate = event.end_date ?? event.endDate

    return {
      id: event.id,
      title: event.title || 'Untitled event',
      description: event.description || '',
      startDate,
      endDate,
      location,
      status: (event.status as TripEvent['status']) || 'upcoming',
      coverImage:
        event.cover_image ||
        event.cover_image_url ||
        event.coverImage ||
        event.coverImageUrl ||
        undefined,
      coverImageFileId:
        event.cover_image_file_id ||
        event.coverImageFileId ||
        event.coverImageFileID ||
        undefined,
      files: Array.isArray(event.files) ? event.files : [],
      notes: event.notes,
      tags: normalizeTags(event.tags),
    }
  }

  useEffect(() => {
    if (preloadedData && preloadedData.length >= 0) {
      const normalized = (preloadedData as any[]).map(transformEvent)
      setEvents(normalized)
      setTotalItems(normalized.length)
      setTotalPages(Math.max(1, Math.ceil(normalized.length / itemsPerPage)))
      setLoading(false)
    } else {
      loadEvents()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId, preloadedData])

  const loadEvents = async () => {
    setLoading(true)
    try {
      console.log('Loading events for trip:', tripId)
      const response = await adminApi.getTripEvents(tripId)
      console.log('Events API response:', response.data)

      const payload = response.data?.data ?? response.data ?? []
      const rawEvents = Array.isArray(payload)
        ? payload
        : Array.isArray(payload.events)
          ? payload.events
          : Array.isArray(payload.items)
            ? payload.items
            : []

      const normalizedEvents = rawEvents.map(transformEvent)
      console.log('Transformed events:', normalizedEvents)
      setEvents(normalizedEvents)

      const pagination = !Array.isArray(payload) && payload.pagination ? payload.pagination : null
      setTotalItems(pagination?.total ?? normalizedEvents.length)
      setTotalPages(
        pagination?.pages ??
        Math.max(1, Math.ceil((pagination?.total ?? normalizedEvents.length) / itemsPerPage))
      )
    } catch (error) {
      console.error('Failed to load events:', error)
      setEvents([])
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      // Handle location - need to create a location first if we have coordinates
      let locationId: string | undefined = undefined
      const { address, lat, lng } = formData.location
      const hasLatLng = typeof lat === 'number' && typeof lng === 'number'

      if (hasLatLng || address) {
        console.log('📍 Creating/updating location:', { address, lat, lng, hasLatLng })

        // If editing an existing event and it has a location, we might want to update that location
        // For now, we'll create a new location each time (or reuse if the backend handles it)
        if (hasLatLng) {
          try {
            const locationData = {
              name: address || 'Event Location',
              address: address || undefined,
              latitude: lat,
              longitude: lng,
            }
            console.log('📍 Calling addTripLocation with:', locationData)
            const locationResponse = await adminApi.addTripLocation(tripId, locationData)
            console.log('📍 Location created:', locationResponse.data)

            // Extract location ID from response
            locationId = locationResponse.data?.data?.id || locationResponse.data?.id
            console.log('📍 Using location ID:', locationId)
          } catch (locationError) {
            console.error('Failed to create location:', locationError)
            // Continue without location rather than failing the whole event save
          }
        }
      }

      const normalizedTags = normalizeTags(formData.tags)
      const locationPayload =
        hasLatLng || address
          ? {
              name: address || 'Event Location',
              address: address || undefined,
              latitude: hasLatLng ? Number(lat) : undefined,
              longitude: hasLatLng ? Number(lng) : undefined,
            }
          : undefined

      const dataToSave: Record<string, any> = {
        title: formData.title,
        description: formData.description || undefined,
        start_date: formData.startDate || undefined,
        end_date: formData.endDate || undefined,
        location_id: locationId,
        status: formData.status,
        cover_image: formData.coverImage || undefined,
        cover_image_file_id: formData.coverImageFileId || undefined,
        tags: normalizedTags,
        trip_id: tripId,
      }

      if (locationPayload) {
        dataToSave.location = locationPayload
      }

      if (userId) {
        dataToSave.created_by = userId
      }

      console.log('📝 Saving event with data:', JSON.stringify(dataToSave, null, 2))

      if (editingEvent) {
        await adminApi.updateTripEvent(tripId, editingEvent.id, dataToSave)
      } else {
        await adminApi.createTripEvent(tripId, dataToSave)
      }
      await loadEvents()
      resetForm()
    } catch (error) {
      console.error('Failed to save event:', error)
      showToast('Failed to save event. Please try again.', { type: 'error' })
    }
  }

  const handleDelete = async (eventId: string) => {
    if (!confirm('Are you sure you want to delete this event?')) return
    try {
      await adminApi.deleteTripEvent(tripId, eventId)
      await loadEvents()
    } catch (error) {
      console.error('Failed to delete event:', error)
      showToast('Failed to delete event. Please try again.', { type: 'error' })
    }
  }

  const handleEdit = (event: TripEvent) => {
    setEditingEvent(event)
    setFormData({
      title: event.title,
      description: event.description,
      startDate: event.startDate ? new Date(event.startDate).toISOString().slice(0, 16) : '',
      endDate: event.endDate ? new Date(event.endDate).toISOString().slice(0, 16) : '',
      status: event.status,
      coverImage: event.coverImage || '',
      coverImageFileId: event.coverImageFileId || '',
      tags: normalizeTags(event.tags),
      location: {
        address: event.location?.address || event.location?.name || '',
        lat: toNumericCoordinate(event.location?.latitude || event.location?.lat),
        lng: toNumericCoordinate(event.location?.longitude || event.location?.lng),
      },
    })
    setShowForm(true)
  }

  const resetForm = () => {
    setShowForm(false)
    setEditingEvent(null)
    setFormData({
      title: '',
      description: '',
      startDate: '',
      endDate: '',
      status: 'upcoming',
      coverImage: '',
      coverImageFileId: '',
      tags: [],
      location: {
        address: '',
        lat: null,
        lng: null,
      },
    })
  }

  const handleEventFileSubmit = async (values: FileMetadataFormValues) => {
    if (!editingEvent) {
      throw new Error('No event selected for file upload.')
    }

    if (values.source === 'upload') {
      if (!values.file) {
        throw new Error('Select a file to upload.')
      }
      await adminApi.uploadFile(values.file, {
        trip_id: tripId,
        event_id: editingEvent.id,
        privacy: values.privacy,
        description: values.description || undefined,
        tags: values.tags,
      })
    } else {
      await adminApi.createTripFile(tripId, {
        filename: values.filename,
        original_filename: values.filename,
        external_url: values.externalUrl,
        description: values.description || undefined,
        tags: values.tags,
        privacy: values.privacy,
        event_id: editingEvent.id,
        file_type: values.fileType,
        file_size: values.fileSize,
      })
    }

    await loadEvents()
    setIsEventFileModalOpen(false)
  }

  const getStatusColor = (status: TripEvent['status']) => {
    switch (status) {
      case 'upcoming': return 'badge-info'
      case 'ongoing': return 'badge-success'
      case 'completed': return 'badge-info'
      case 'cancelled': return 'badge-danger'
    }
  }

  // Paginate events for display
  const paginatedEvents = events.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  const coverImageValue: UploadedFile | null = formData.coverImage
    ? {
        id: formData.coverImageFileId || 'event-cover',
        url: formData.coverImage,
        thumbnailUrl: formData.coverImage,
        fileType: 'image',
        size: 0,
        filename: formData.coverImage.split('/').pop() || 'event-cover',
        originalFilename: formData.coverImage.split('/').pop() || undefined,
      }
    : null

  if (loading) {
    return <div className="text-center py-8 text-gray-500">Loading events...</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Trip Events</h3>
          <p className="text-sm text-gray-600 mt-1">Manage events and activities for this trip</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Event
        </button>
      </div>

      {/* Event Form */}
      {showForm && (
        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
          <h4 className="text-md font-semibold text-gray-900 mb-4">
            {editingEvent ? 'Edit Event' : 'New Event'}
          </h4>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                  className="input w-full"
                  placeholder="Meet with Friends"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                  className="input w-full min-h-[100px]"
                  placeholder="Buy gifts to Lolly and maybe visit Kyle..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <input
                  type="datetime-local"
                  value={formData.startDate}
                  onChange={(e) => setFormData((prev) => ({ ...prev, startDate: e.target.value }))}
                  className="input w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <input
                  type="datetime-local"
                  value={formData.endDate}
                  onChange={(e) => setFormData((prev) => ({ ...prev, endDate: e.target.value }))}
                  className="input w-full"
                  min={formData.startDate}
                />
              </div>

              <div className="md:col-span-2">
                <LocationPicker
                  label="Location"
                  description="Search for the event location or drop a pin on the map."
                  value={formData.location}
                  onChange={(location: LocationValue) => setFormData((prev) => ({ ...prev, location }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value as TripEvent['status'] }))}
                  className="input w-full"
                >
                  <option value="upcoming">Upcoming</option>
                  <option value="ongoing">Ongoing</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <SingleFileUploader
                  label="Cover Image"
                  helperText="Upload a banner image for the event."
                  value={coverImageValue}
                  onChange={(file) => {
                    setFormData((prev) => ({
                      ...prev,
                      coverImage: file?.url || '',
                      coverImageFileId: file?.id || '',
                    }))
                  }}
                  tripId={tripId}
                  userId={userId}
                  privacy="public"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags
                </label>
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2">
                    {formData.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              tags: prev.tags.filter((_, i) => i !== index)
                            }))
                          }}
                          className="ml-1 text-blue-600 hover:text-blue-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <input
                    type="text"
                    className="input w-full"
                    placeholder="Add a tag and press Enter"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        const value = (e.target as HTMLInputElement).value.trim()
                        if (value && !formData.tags.includes(value)) {
                          setFormData((prev) => ({
                            ...prev,
                            tags: [...prev.tags, value]
                          }))
                          ;(e.target as HTMLInputElement).value = ''
                        }
                      }
                    }}
                  />
                </div>
              </div>

              {/* Files section for existing events */}
              {editingEvent && (
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Attached Files
                    </label>
                    <button
                      type="button"
                      onClick={() => setIsEventFileModalOpen(true)}
                      className="btn-secondary flex items-center gap-2 text-sm"
                    >
                      <Plus className="h-4 w-4" />
                      Add File
                    </button>
                  </div>
                  {Array.isArray(editingEvent.files) && editingEvent.files.length > 0 ? (
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <div className="flex flex-wrap gap-2">
                        {editingEvent.files.map((fileItem: any, fileIndex: number) => {
                          const file =
                            typeof fileItem === 'string'
                              ? { id: fileItem, filename: fileItem }
                              : fileItem || {}
                          const href = file.url || file.file_url || file.external_url
                          const label =
                            file.filename ||
                            file.original_filename ||
                            file.name ||
                            file.id ||
                            `File ${fileIndex + 1}`
                          const key = file.id || `${editingEvent.id}-file-${fileIndex}`

                          if (href) {
                            return (
                              <a
                                key={key}
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700 text-sm hover:bg-gray-50 hover:border-gray-300 transition-colors"
                              >
                                <FileText className="h-4 w-4" />
                                {label}
                              </a>
                            )
                          }

                          return (
                            <span
                              key={key}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700 text-sm"
                            >
                              <FileText className="h-4 w-4" />
                              {label}
                            </span>
                          )
                        })}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No files attached yet</p>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={resetForm}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary flex-1"
              >
                {editingEvent ? 'Update Event' : 'Create Event'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Events List */}
      <div className="bg-white rounded-lg border border-gray-200">
        {events.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">No events yet</p>
            <p className="text-sm text-gray-500 mt-1">Add events to organize your trip activities</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left">
                    <th className="px-6 py-3 font-medium text-gray-700">Title</th>
                    <th className="px-6 py-3 font-medium text-gray-700">Date</th>
                    <th className="px-6 py-3 font-medium text-gray-700">Location</th>
                    <th className="px-6 py-3 font-medium text-gray-700">Status</th>
                    <th className="px-6 py-3 font-medium text-gray-700">Files</th>
                    <th className="px-6 py-3 font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedEvents.map((event) => (
                    <tr key={event.id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {event.coverImage && (
                            <img
                              src={event.coverImage}
                              alt={event.title}
                              className="w-12 h-12 rounded-lg object-cover"
                            />
                          )}
                          <div>
                            <p
                              className="font-medium text-gray-900 cursor-pointer hover:text-blue-600 transition-colors"
                              onClick={() => handleEdit(event)}
                            >
                              {event.title}
                            </p>
                            {event.description && (
                              <p className="text-sm text-gray-600 line-clamp-1 max-w-md">
                                {event.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          {new Date(event.startDate).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {event.location?.address ? (
                          <div className="flex items-center gap-1.5">
                            <MapPin className="h-4 w-4 text-gray-400" />
                            <span className="line-clamp-1 max-w-[200px]">
                              {event.location.address}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`badge ${getStatusColor(event.status)}`}>
                          {event.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {Array.isArray(event.files) && event.files.length > 0 ? (
                          <div className="flex items-center gap-1.5">
                            <FileText className="h-4 w-4 text-gray-400" />
                            <span>{event.files.length}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(event)}
                            className="px-3 py-1.5 bg-gray-100 hover:bg-blue-50 text-blue-600 rounded-lg text-sm font-medium transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(event.id)}
                            className="px-3 py-1.5 bg-gray-100 hover:bg-red-50 text-red-600 rounded-lg text-sm font-medium transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              itemsPerPage={itemsPerPage}
              totalItems={totalItems}
            />
          </>
        )}
      </div>
      <FileMetadataModal
        open={isEventFileModalOpen}
        mode="create"
        onClose={() => {
          setIsEventFileModalOpen(false)
        }}
        onSubmit={handleEventFileSubmit}
        events={events.map(event => ({ id: event.id, title: event.title }))}
        initialData={editingEvent ? { eventId: editingEvent.id } : undefined}
        contextLabel={editingEvent ? `Attach file to "${editingEvent.title}"` : 'Attach file to event'}
      />
    </div>
  )
}
